// Anexos originais da tabela de frete (PO1-2857 / PO1-2859): lista, anexar, abrir e excluir.
// Componente único usado no Resumo da lista, na aba Dados do TabelaFreteModal, na tela de OCR
// (NovaTabelaFrete) e no importador XLSM. Autocontido: estado, chamadas e erros vivem aqui.
//
// Props:
//   tabelaId        id da FreteTabela (sem id → mostra a dica "salve a tabela para anexar")
//   anexosIniciais  Anexo[] opcional; quando o pai já tem `anexos[]` (GET /frete/tabelas/:id),
//                   evita uma chamada. Se undefined, o componente busca a lista.
//   onChange(lista, mensagem) chamado depois de anexar/excluir (o pai decide se recarrega algo;
//   a mensagem serve para o pai exibir o feedback quando ele desmonta este bloco ao recarregar)
//   titulo, descricao, compact
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button } from "reactstrap";
import { ClipLoader } from "react-spinners";

import formatBytes from "../../../utils/formatBytes";
import { formatDateTime } from "../../../utils/formatDate";
import {
  ANEXO_TABELA_ACCEPT,
  ANEXO_TABELA_LEGENDA,
  ANEXO_TABELA_MAX_ARQUIVOS,
  validarArquivosAnexo,
  listarAnexos,
  enviarAnexos,
  obterUrlAnexo,
  excluirAnexo,
  mensagemErroAnexo,
  rotuloOrigem,
} from "../../../services/freteTabelaAnexosApi";

const ICONE_POR_FORMATO = {
  pdf: "tim-icons icon-paper",
  xls: "tim-icons icon-notes",
  xlsx: "tim-icons icon-notes",
  xlsm: "tim-icons icon-notes",
  msg: "tim-icons icon-email-85",
  eml: "tim-icons icon-email-85",
};

const containerStyle = {
  border: "1px solid var(--everlog-card-border)",
  borderRadius: 12,
  background: "var(--everlog-card-surface-field)",
  marginTop: "1rem",
};
const tituloStyle = {
  color: "var(--everlog-card-muted)",
  fontSize: "0.76rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontWeight: 600,
};
const descricaoStyle = { color: "var(--everlog-card-muted)", fontSize: "0.78rem", marginTop: 2 };
const legendaStyle = { color: "var(--everlog-card-muted)", fontSize: "0.72rem", display: "block", marginTop: 4 };
const linhaStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.6rem",
  padding: "0.45rem 0",
  borderBottom: "1px solid var(--everlog-card-border)",
};
const nomeStyle = {
  background: "none",
  border: "none",
  padding: 0,
  color: "var(--theme-accent)",
  cursor: "pointer",
  textAlign: "left",
  fontSize: "0.88rem",
  fontWeight: 500,
  wordBreak: "break-word",
};
const metaStyle = { color: "var(--everlog-card-muted)", fontSize: "0.74rem", marginTop: 2 };
const acaoStyle = { padding: "0 6px", color: "var(--theme-accent)", cursor: "pointer" };
const acaoPerigoStyle = { padding: "0 6px", color: "var(--everlog-status-danger)", cursor: "pointer" };
const vazioStyle = { color: "var(--everlog-card-muted)", fontSize: "0.82rem", padding: "0.4rem 0" };
const erroStyle = {
  background: "var(--everlog-status-danger-bg)",
  border: "1px solid var(--everlog-status-danger-border)",
  color: "var(--everlog-status-danger)",
  borderRadius: 8,
  padding: "0.5rem 0.75rem",
  fontSize: "0.82rem",
  marginTop: "0.5rem",
};
const avisoStyle = {
  background: "var(--theme-accent-glow)",
  border: "1px solid var(--theme-accent-border)",
  color: "var(--everlog-card-text)",
  borderRadius: 8,
  padding: "0.5rem 0.75rem",
  fontSize: "0.82rem",
  marginTop: "0.5rem",
};

// Abre a URL pré-assinada em nova aba sem cair no bloqueador de pop-up: a aba é aberta
// de forma síncrona (no clique) e recebe a URL depois do await. Se o navegador devolver
// null (pop-up bloqueado), cai no <a target=_blank> programático.
async function abrirEmNovaAba(obterUrl) {
  let win = null;
  try {
    win = window.open("", "_blank");
    if (win) win.opener = null;
  } catch (e) {
    win = null;
  }
  try {
    const url = await obterUrl();
    if (win && !win.closed) {
      win.location.href = url;
      return;
    }
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (err) {
    if (win && !win.closed) win.close();
    throw err;
  }
}

function formatarData(valor) {
  if (!valor) return "";
  try {
    return formatDateTime(valor) || "";
  } catch (e) {
    return "";
  }
}

export default function AnexosTabela({
  tabelaId,
  anexosIniciais,
  onChange,
  titulo = "Anexos originais",
  descricao,
  compact = false,
}) {
  const [anexos, setAnexos] = useState(Array.isArray(anexosIniciais) ? anexosIniciais : []);
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [abrindoId, setAbrindoId] = useState(null);
  const [excluindoId, setExcluindoId] = useState(null);
  const [confirmandoId, setConfirmandoId] = useState(null);
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");
  const fileInputRef = useRef(null);
  const montadoRef = useRef(true);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  const recarregar = useCallback(async () => {
    if (!tabelaId) return null;
    setCarregando(true);
    try {
      const lista = await listarAnexos(tabelaId);
      if (montadoRef.current) setAnexos(lista);
      return lista;
    } catch (err) {
      if (montadoRef.current) {
        setErro(mensagemErroAnexo(err, "Não foi possível carregar os anexos."));
      }
      return null;
    } finally {
      if (montadoRef.current) setCarregando(false);
    }
  }, [tabelaId]);

  // Trocou de tabela: limpa mensagens e confirmação pendente.
  useEffect(() => {
    setConfirmandoId(null);
    setErro("");
    setAviso("");
  }, [tabelaId]);

  // Carrega a lista: da prop (pai já tem anexos[]) ou do backend. Quando o pai recarrega a
  // lista depois de um anexar/excluir (ex.: painel Resumo), as mensagens de sucesso ficam.
  useEffect(() => {
    if (!tabelaId) {
      setAnexos([]);
      return;
    }
    if (Array.isArray(anexosIniciais)) {
      setAnexos(anexosIniciais);
      return;
    }
    recarregar();
  }, [tabelaId, anexosIniciais, recarregar]);

  const notificarMudanca = useCallback(
    (lista, mensagem) => {
      if (typeof onChange === "function") onChange(lista || [], mensagem || "");
    },
    [onChange]
  );

  const handleSelecionar = async (event) => {
    const { validos, erros } = validarArquivosAnexo(event.target.files);
    event.target.value = "";
    setErro("");
    setAviso("");
    if (erros.length) setErro(erros.join(" "));
    if (!validos.length) return;

    setEnviando(true);
    try {
      await enviarAnexos(tabelaId, validos);
      const lista = await recarregar();
      const mensagem = `${validos.length} arquivo(s) anexado(s).`;
      setAviso(mensagem);
      notificarMudanca(lista, mensagem);
    } catch (err) {
      setErro(mensagemErroAnexo(err, "Não foi possível anexar o arquivo."));
    } finally {
      if (montadoRef.current) setEnviando(false);
    }
  };

  const handleAbrir = async (anexo) => {
    setErro("");
    setAbrindoId(anexo.id);
    try {
      await abrirEmNovaAba(async () => {
        const resposta = await obterUrlAnexo(tabelaId, anexo.id);
        if (!resposta || !resposta.url) throw new Error("URL do anexo não disponível.");
        return resposta.url;
      });
    } catch (err) {
      setErro(mensagemErroAnexo(err, "Não foi possível abrir o anexo."));
    } finally {
      if (montadoRef.current) setAbrindoId(null);
    }
  };

  const handleExcluir = async (anexo) => {
    setErro("");
    setAviso("");
    setExcluindoId(anexo.id);
    try {
      await excluirAnexo(tabelaId, anexo.id);
      setConfirmandoId(null);
      const lista = await recarregar();
      const mensagem = `Anexo "${anexo.nome}" excluído.`;
      setAviso(mensagem);
      notificarMudanca(lista, mensagem);
    } catch (err) {
      setErro(mensagemErroAnexo(err, "Não foi possível excluir o anexo."));
    } finally {
      if (montadoRef.current) setExcluindoId(null);
    }
  };

  const ocupado = enviando || carregando;
  const padding = compact ? "0.7rem 0.8rem" : "0.9rem 1rem";

  if (!tabelaId) {
    return (
      <div style={{ ...containerStyle, padding }}>
        <div style={tituloStyle}>
          <i className="tim-icons icon-attach-87" style={{ marginRight: 6 }} />
          {titulo}
        </div>
        <div style={vazioStyle}>
          Salve a tabela para anexar o arquivo original (PDF, planilha ou e-mail do carrier).
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...containerStyle, padding }} data-testid="anexos-tabela">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={tituloStyle}>
            <i className="tim-icons icon-attach-87" style={{ marginRight: 6 }} />
            {titulo}
            {anexos.length > 0 ? ` (${anexos.length})` : ""}
          </div>
          {descricao ? <div style={descricaoStyle}>{descricao}</div> : null}
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ANEXO_TABELA_ACCEPT}
            style={{ display: "none" }}
            disabled={ocupado}
            onChange={handleSelecionar}
            data-testid="anexos-tabela-input"
          />
          <Button
            outline
            color="primary"
            size="sm"
            disabled={ocupado}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            title={`Anexar até ${ANEXO_TABELA_MAX_ARQUIVOS} arquivos por envio`}
          >
            {enviando ? (
              <ClipLoader size={11} color="var(--theme-accent)" />
            ) : (
              <i className="tim-icons icon-cloud-upload-94" />
            )}{" "}
            {enviando ? "Enviando..." : "Anexar"}
          </Button>
        </div>
      </div>
      <small style={legendaStyle}>{ANEXO_TABELA_LEGENDA}</small>

      {erro ? <div style={erroStyle}>{erro}</div> : null}
      {aviso && !erro ? <div style={avisoStyle}>{aviso}</div> : null}

      <div style={{ marginTop: "0.5rem" }}>
        {carregando && anexos.length === 0 ? (
          <div style={{ padding: "0.6rem 0" }}>
            <ClipLoader size={16} color="var(--theme-accent)" />
          </div>
        ) : anexos.length === 0 ? (
          <div style={vazioStyle}>
            Nenhum anexo. Anexe o PDF, a planilha ou o e-mail original desta tabela.
          </div>
        ) : (
          anexos.map((anexo) => {
            const formato = String(anexo.formato || "").toLowerCase();
            const herdado = rotuloOrigem(anexo);
            const confirmando = confirmandoId === anexo.id;
            const excluindo = excluindoId === anexo.id;
            const abrindo = abrindoId === anexo.id;
            return (
              <div key={anexo.id} style={linhaStyle}>
                <i
                  className={ICONE_POR_FORMATO[formato] || "tim-icons icon-single-copy-04"}
                  style={{ color: "var(--everlog-card-muted)", fontSize: "1rem", flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <button
                    type="button"
                    style={nomeStyle}
                    onClick={() => handleAbrir(anexo)}
                    disabled={abrindo}
                    title="Abrir o arquivo em nova aba"
                  >
                    {anexo.nome}
                  </button>
                  <div style={metaStyle}>
                    <Badge color="secondary" pill style={{ marginRight: 6 }}>
                      {formato.toUpperCase() || "?"}
                    </Badge>
                    {formatBytes(anexo.tamanho)}
                    {anexo.creationTime ? ` · ${formatarData(anexo.creationTime)}` : ""}
                    {herdado ? (
                      <Badge color="info" pill style={{ marginLeft: 6 }}>
                        {herdado}
                      </Badge>
                    ) : null}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.15rem", flexShrink: 0 }}>
                  {confirmando ? (
                    <>
                      <span style={{ color: "var(--everlog-status-danger)", fontSize: "0.76rem", marginRight: 4 }}>
                        Excluir?
                      </span>
                      <Button
                        color="danger"
                        size="sm"
                        disabled={excluindo}
                        onClick={() => handleExcluir(anexo)}
                      >
                        {excluindo ? "Excluindo..." : "Confirmar"}
                      </Button>
                      <Button
                        color="secondary"
                        size="sm"
                        outline
                        disabled={excluindo}
                        onClick={() => setConfirmandoId(null)}
                      >
                        Cancelar
                      </Button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        style={acaoStyle}
                        title="Abrir"
                        disabled={abrindo}
                        onClick={() => handleAbrir(anexo)}
                      >
                        {abrindo ? (
                          <ClipLoader size={10} color="var(--theme-accent)" />
                        ) : (
                          <i className="tim-icons icon-cloud-download-93" />
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        style={acaoPerigoStyle}
                        title="Excluir anexo"
                        disabled={ocupado}
                        onClick={() => {
                          setErro("");
                          setAviso("");
                          setConfirmandoId(anexo.id);
                        }}
                      >
                        <i className="tim-icons icon-trash-simple" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
